import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuotaErrorSurface } from "@/hooks/useQuotaErrorSurface";

interface FetchLessonParams {
  native_lang: string;
  learning_lang: string;
  level: string;
  lesson_index: number;
}

export interface LessonData {
  title: string;
  description: string;
  vocabulary: Array<{ word: string; translation: string; example: string }>;
  phrases: Array<{ phrase: string; translation: string; context: string }>;
  expressions: Array<{ expression: string; meaning: string; literal: string }>;
}

export function useLanguageLesson() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const surfaceQuota = useQuotaErrorSurface();

  // useCallback with empty deps keeps the function identity stable across
  // renders. Consumers that put `fetchLesson` in an effect dep array (or,
  // preferably, don't) won't re-trigger the effect on every parent render.
  const fetchLesson = useCallback(async (params: FetchLessonParams): Promise<LessonData | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("language-lesson", {
        body: params,
      });
      if (fnError) {
        if (await surfaceQuota(fnError)) { setLoading(false); return null; }
        throw fnError;
      }
      const validItems = (items: unknown, fields: string[]) => Array.isArray(items) && items.length > 0 && items.every(item => item && fields.every(field => typeof item[field] === "string" && item[field].trim()));
      if (typeof data?.title !== "string" || !data.title.trim() || typeof data.description !== "string" || !validItems(data.vocabulary, ["word", "translation"]) || !validItems(data.phrases, ["phrase", "translation"]) || !validItems(data.expressions, ["expression", "meaning"])) throw new Error("Invalid lesson response");
      return data as LessonData;
    } catch (err: any) {
      console.error("[useLanguageLesson] fetch failed:", err);
      setError(err?.message || "Unknown error");
      return null;
    } finally {
      setLoading(false);
    }
  }, [surfaceQuota]);

  return { fetchLesson, loading, error };
}
